import { useState, useRef, useEffect, useCallback } from "react";
import { HexColorPicker } from "react-colorful";

interface ColorPickerProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
}

export function ColorPicker({ label, value, onChange }: ColorPickerProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Sync inputValue when value prop changes externally
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleHexInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let raw = e.target.value;
            // Ensure leading #
            if (!raw.startsWith("#")) raw = "#" + raw.replace(/#/g, "");
            setInputValue(raw);
            // Only propagate valid 6-digit hex
            if (/^#[0-9A-Fa-f]{6}$/.test(raw)) {
                onChange(raw);
            }
        },
        [onChange]
    );

    const handlePickerChange = useCallback(
        (color: string) => {
            setInputValue(color);
            onChange(color);
        },
        [onChange]
    );

    const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(value);

    return (
        <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-white leading-tight">
                {label}
            </label>

            <div className="relative">
                {/* Swatch + hex row */}
                <div className="flex items-center gap-2">
                    <button
                        ref={triggerRef}
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        className="w-8 h-8 rounded-md border border-[#79747E] shadow-sm flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-transform active:scale-95"
                        style={{ backgroundColor: isValidHex ? value : "#ffffff" }}
                        aria-label={`Pick color for ${label}`}
                    />
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleHexInput}
                        maxLength={7}
                        spellCheck={false}
                        className="w-full text-xs font-mono rounded-md border border-[#79747E] text-white bg-[#0F0F0F] px-2 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="#000000"
                    />
                </div>

                {/* Popover */}
                {open && (
                    <div
                        ref={popoverRef}
                        className="absolute z-50 top-full mt-2 left-0 bg-white rounded-xl shadow-xl border border-gray-100 p-3 flex flex-col gap-3"
                        style={{ minWidth: 200 }}
                    >
                        {/* react-colorful picker */}
                        <HexColorPicker
                            color={isValidHex ? value : "#ffffff"}
                            onChange={handlePickerChange}
                            style={{ width: "100%", height: 160 }}
                        />

                        {/* Hex input inside popover for convenience */}
                        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50">
                            <div
                                className="w-4 h-4 rounded flex-shrink-0 border border-gray-200"
                                style={{ backgroundColor: isValidHex ? value : "#ffffff" }}
                            />
                            <input
                                type="text"
                                value={inputValue}
                                onChange={handleHexInput}
                                maxLength={7}
                                spellCheck={false}
                                className="w-full text-xs font-mono bg-transparent text-gray-700 focus:outline-none"
                                placeholder="#000000"
                            />
                        </div>

                        {/* Quick presets */}
                        <div className="grid grid-cols-8 gap-1">
                            {[
                                "#000000", "#ffffff", "#f87171", "#fb923c",
                                "#facc15", "#4ade80", "#60a5fa", "#c084fc",
                                "#f472b6", "#94a3b8", "#1e293b", "#0f172a",
                            ].map((preset) => (
                                <button
                                    key={preset}
                                    type="button"
                                    onClick={() => handlePickerChange(preset)}
                                    className="w-5 h-5 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-transform hover:scale-110 active:scale-95"
                                    style={{ backgroundColor: preset }}
                                    aria-label={preset}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
